import React, { useEffect, useCallback } from 'react';
import { useInvoiceInputs } from '@/components/user_configuration/user_invoice/invoice-inputs';
import { InvoiceConfigForm } from '@/components/config_forms/invoice/InvoiceConfigForm';
import { useUserConfigDispatch } from '@/context/UserConfigContext';
import { sortReminderDays, updateReminderDayAtIndex } from '@/components/config_forms/invoice/utils/reminderDays';
import { focusNewReminderChip } from '@/components/config_forms/invoice/utils/focusManagement';
import { REMINDER_DAYS_GROUP_VALIDATION_KEY } from '@/utils/validation/schemas/invoice';
import type { UserConfigurationState } from '@/types/userConfiguration';

type InvoiceConfigProps = Pick<
  UserConfigurationState,
  | 'paymentTermsDays'
  | 'lateFeePercentage'
  | 'lateFeeFixedAmount'
  | 'recurringLateFeeInterval'
  | 'recurringLateFee'
  | 'earlyPaymentDiscountPercentage'
  | 'earlyPaymentFixedAmount'
  | 'earlyPaymentDays'
  | 'useLateFee'
  | 'lateFeeType'
  | 'useEarlyPaymentDiscount'
  | 'earlyPaymentType'
  | 'autoSendReminders'
  | 'employmentType'
  | 'reminderDays'
  | 'useCancellationPolicy'
  | 'cancellationPolicy'
  | 'rushPremiumPolicy'
>;

export const InvoiceConfig: React.FC<InvoiceConfigProps> = React.memo((props) => {
  const { updateInvoiceConfig } = useUserConfigDispatch();
  const { 
    useLateFee,
    lateFeeType,
    useEarlyPaymentDiscount,
    earlyPaymentType,
    autoSendReminders, 
    employmentType,
    paymentTermsDays,
    useCancellationPolicy,
  } = props;

  // Only sort non-zero values, keep zeros (new chips) at the end
  const reminderDays = sortReminderDays(props.reminderDays);

  const [localAutoSendReminders, setLocalAutoSendReminders] = React.useState(autoSendReminders);
  const [localUseLateFee, setLocalUseLateFee] = React.useState(useLateFee);
  const [localLateFeeType, setLocalLateFeeType] = React.useState<'percentage' | 'fixed'>(lateFeeType || 'percentage');
  const [localUseEarlyPaymentDiscount, setLocalUseEarlyPaymentDiscount] = React.useState(useEarlyPaymentDiscount);
  const [localEarlyPaymentType, setLocalEarlyPaymentType] = React.useState<'percentage' | 'fixed'>(earlyPaymentType || 'percentage');
  
  // NEW – local toggle state
  const [localUseCancellationPolicy, setLocalUseCancellationPolicy] = React.useState(useCancellationPolicy);

  const handleToggleCancellationPolicy = useCallback((checked: boolean) => {
    setLocalUseCancellationPolicy(checked);
    updateInvoiceConfig({ useCancellationPolicy: checked });
  }, [updateInvoiceConfig]);
  
  const removeReminderDay = React.useCallback((index: number) => {
    updateInvoiceConfig({ reminderDays: reminderDays.filter((_, i) => i !== index) });
  }, [reminderDays, updateInvoiceConfig]);

  const updateReminderDayInStateCallback = React.useCallback((idx: number, newValue: number | null) => {
    const newDays = updateReminderDayAtIndex(reminderDays, idx, newValue);
    updateInvoiceConfig({ reminderDays: newDays });
  }, [reminderDays, updateInvoiceConfig]);

  const handleChipValidationComplete = React.useCallback((
    idx: number,
    isValid: boolean,
    validatedValue: number | null,
    originalValueBeforeEdit: number 
  ) => {
    // If a chip was newly added (started as 0) and is still empty/invalid (value is 0 or null after edit),
    // then remove it. The `idx` here is the current index in the sorted `reminderDays` array.
    if (originalValueBeforeEdit === 0 && !isValid && (validatedValue === null || validatedValue === 0)) {
      removeReminderDay(idx);
    }
    // For existing chips that become invalid, or new chips that have a non-zero invalid value,
    // the chip validation will show an inline error, and the chip remains for the user to correct.
  }, [removeReminderDay]);
  
  // Reminder days group validation is handled by the Zod schema at the provider level.
  // The ValidationProvider + useValidationToasts handles toast display automatically.


  const addEmptyReminder = useCallback(() => {
    // `reminderDays` is the component's current, sorted list of reminder days.
    // (Non-zero values sorted, followed by all zero values).
    // The new chip will be appended to this list, so its index will be the current length.
    const newChipVisualIndex = reminderDays.length;

    // Create the new array for the state by appending 0.
    // The component's local `reminderDays` derivation will re-sort this correctly on the next render.
    const newDaysForState = [...reminderDays, 0];
    updateInvoiceConfig({ reminderDays: newDaysForState });

    // Focus the new chip after DOM update
    focusNewReminderChip(newChipVisualIndex);
  }, [reminderDays, updateInvoiceConfig]);

  const {
    paymentTermsInput,
    lateFeePctInput,
    lateFeeFixedInput,
    recurringIntervalInput,
    earlyDiscountPctInput,
    earlyDiscountFixedInput,
    earlyDaysInput,
  } = useInvoiceInputs(
    props,
    updateInvoiceConfig,
    localUseLateFee,
    localLateFeeType,
    localUseEarlyPaymentDiscount,
    localEarlyPaymentType
  );

  const shouldShowEarlyPaymentDaysInput = localUseEarlyPaymentDiscount;

  // Sync local state with global state if global state changes (e.g. on initial load)
  useEffect(() => {
    setLocalAutoSendReminders(autoSendReminders);
  }, [autoSendReminders]);

  useEffect(() => {
    setLocalUseLateFee(useLateFee);
    setLocalLateFeeType(lateFeeType || 'percentage');
  }, [useLateFee, lateFeeType]);

  useEffect(() => {
    setLocalUseEarlyPaymentDiscount(useEarlyPaymentDiscount);
    setLocalEarlyPaymentType(earlyPaymentType || 'percentage');
  }, [useEarlyPaymentDiscount, earlyPaymentType]);

  useEffect(() => {
    setLocalUseCancellationPolicy(useCancellationPolicy);
  }, [useCancellationPolicy]);

  return (
    <InvoiceConfigForm
      title="Default Invoice Configuration"
      description="Configure your standard invoice settings and payment terms"
      paymentTermsDays={paymentTermsDays}
      paymentTermsProps={paymentTermsInput.inputProps}
      paymentTermsError={paymentTermsInput.errorElement}
      useLateFee={localUseLateFee}
      lateFeeType={localLateFeeType}
      recurringLateFee={props.recurringLateFee ?? 'once'}
      onToggleLateFee={(checked) => {
        setLocalUseLateFee(checked);
        updateInvoiceConfig({ useLateFee: checked });
      }}
      onLateFeeTypeChange={(val) => {
        setLocalLateFeeType(val);
        updateInvoiceConfig({ lateFeeType: val });
      }}
      onRecurringLateFeeChange={(val) => {
        updateInvoiceConfig({
          recurringLateFee: val as 'daily' | 'interval'
        });
      }}
      onIsRecurringLateFeeChange={(checked) => {
        updateInvoiceConfig({
          isRecurringLateFee: checked,
          recurringLateFee: checked ? 'daily' : 'once'
        });
      }}
      lateFeePctProps={lateFeePctInput.inputProps}
      lateFeeFixedProps={lateFeeFixedInput.inputProps}
      recurringIntervalProps={recurringIntervalInput.inputProps}
      lateFeePctError={lateFeePctInput.errorElement}
      lateFeeFixedError={lateFeeFixedInput.errorElement}
      recurringIntervalError={recurringIntervalInput.errorElement}
      useEarlyPaymentDiscount={localUseEarlyPaymentDiscount}
      earlyPaymentType={localEarlyPaymentType}
      onToggleEarlyPayment={(checked) => {
        setLocalUseEarlyPaymentDiscount(checked);
        updateInvoiceConfig({ useEarlyPaymentDiscount: checked });
      }}
      onEarlyPaymentTypeChange={(val) => {
        setLocalEarlyPaymentType(val);
        updateInvoiceConfig({ earlyPaymentType: val });
      }}
      earlyDiscountPctProps={earlyDiscountPctInput.inputProps}
      earlyDiscountFixedProps={earlyDiscountFixedInput.inputProps}
      earlyDaysProps={earlyDaysInput.inputProps}
      earlyDiscountPctError={earlyDiscountPctInput.errorElement}
      earlyDiscountFixedError={earlyDiscountFixedInput.errorElement}
      earlyDaysError={earlyDaysInput.errorElement}
      useCancellationPolicy={localUseCancellationPolicy}
      cancellationPolicy={props.cancellationPolicy}
      onToggleCancellationPolicy={handleToggleCancellationPolicy}
      onCancellationPolicyChange={(newRules) => updateInvoiceConfig({ cancellationPolicy: newRules as UserConfigurationState['cancellationPolicy'] })}
      onDeactivateCancellationPolicy={() => handleToggleCancellationPolicy(false)}
      rushPremiumPolicy={props.rushPremiumPolicy}
      onRushPremiumPolicyChange={(newRules) => updateInvoiceConfig({ rushPremiumPolicy: newRules })}
      autoSendReminders={localAutoSendReminders}
      reminderDays={reminderDays}
      validationKey={REMINDER_DAYS_GROUP_VALIDATION_KEY}
      onToggleAutoSendReminders={(checked) => {
        setLocalAutoSendReminders(checked);
        updateInvoiceConfig({ autoSendReminders: checked });
      }}
      onUpdateReminderDay={updateReminderDayInStateCallback}
      onValidationComplete={handleChipValidationComplete}
      onRemoveReminderDay={removeReminderDay}
      onAddEmptyReminder={addEmptyReminder}
      onClearGroupError={() => {}} // Validation errors auto-clear via provider when data changes
      employmentType={employmentType}
    />
  );
});

InvoiceConfig.displayName = 'InvoiceConfig';
